// Direct module imports, not the filters barrel: the route's `validateSearch` loads this file
// in the eagerly loaded route tree, and the barrel would pull the whole filter UI into the
// boot bundle.
import { getOperatorsForFieldType } from '@client/components/filters/core/operators';
import type { Filter, FilterConfig } from '@client/components/filters/core/types';
import { getPathStateKey } from '@client/hooks/navigation/usePathState';
import { usePathStateStore } from '@client/stores/PathStateStore';
import { FilterOperatorSchema, FilterValueSchema } from '@shared/schemas/filter';
import { z } from 'zod';

/**
 * CUR-706 — the compliance-audit list's URL filter parameter, in two halves.
 *
 * A filter set may be supplied in the URL as a one-shot entry instruction; the shared
 * filter bar consumes it on mount and strips it back out (`useFilterBuilderWithUrl`),
 * and reload persistence comes from session path state, not from the link. See
 * `openspec/specs/compliance-audit-search-page/spec.md` — the address bar deliberately
 * does not mirror the active filters.
 *
 * The promise both halves exist to keep: a stale or hand-edited link degrades to an
 * unfiltered list, never to a crash and never to a PARTIALLY applied set — a link that
 * silently loses one of its filters shows a narrower result than it asked for, with
 * nothing on screen saying so.
 *
 * Lives beside the route rather than in it because a route module may only export its
 * `Route` at runtime, and both halves are worth testing directly.
 */

/** Ids with a dedicated variant below; the fallback must not also accept them. */
const RESERVED_FILTER_IDS = ['auditor', 'template'] as const;

/**
 * The SHAPE half. Every variant has to be bounded for `.catch(undefined)` to mean
 * anything: a permissive `value: z.any()` catch-all would swallow the very failures the
 * `.catch` exists to absorb and hand `buildWhereClause` a value it cannot build from
 * (`{ filterId: 'search', value: {} }` reaching `.trim()`, a non-numeric `template` id
 * reaching the server). So the two id-typed branches pin their operators and coerce
 * their ids back to bigint (a filter value makes the round trip through the URL as a
 * string), and the fallback covers only the remaining ids with a real `FilterValueSchema`.
 */
export const ComplianceAuditSearchSchema = z.object({
    'compliance-audit-filters': z
        .array(
            z.union([
                z.object({
                    id: z.string(),
                    filterId: z.literal('auditor'),
                    operator: z.enum(['in', 'notIn']),
                    value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.literal('template'),
                    operator: z.literal('in'),
                    value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                }),
                z.object({
                    id: z.string(),
                    filterId: z
                        .string()
                        .refine(
                            (filterId) => !RESERVED_FILTER_IDS.some((reserved) => reserved === filterId),
                            'Handled by a dedicated variant',
                        ),
                    operator: FilterOperatorSchema,
                    value: FilterValueSchema,
                }),
            ]),
        )
        .optional()
        .catch(undefined),
});

/**
 * Ids whose value set is closed — the field's static `options` are every value the server
 * accepts. `status` is the `ComplianceAuditStatus` enum, and a label can leave it (CUR-1624
 * replaced COMPLETED with PASSED / FAILED): a saved link still carrying the old value would
 * otherwise pass the id/operator check and fail the list request with BAD_REQUEST instead
 * of degrading. The template and auditor pickers are open sets (their options load or are
 * searched), so their ids are validated by the schema variants above instead.
 */
const CLOSED_VALUE_FILTER_IDS = ['status'] as const;

/**
 * The SUPPORT half: the parsed set, or `undefined` when any entry names something this
 * list does not offer.
 *
 * Which ids exist and which operators each one permits belongs to the filter config, so
 * it is checked against the config itself rather than re-enumerated in Zod — a second
 * list would drift the first time a filter is added or its operators are narrowed.
 * `buildWhereClause` silently skips a pair it cannot build (`status` with `contains`, an
 * id belonging to another list), which is the partial application above; one unsupported
 * entry therefore drops the whole set, matching what a malformed one already gets. A
 * closed-value field (above) is also held to its options, so a retired enum value is
 * unsupported too.
 */
export function acceptDeepLinkFilters<TWhere>(
    config: FilterConfig<TWhere>,
    filters: Filter[] | undefined,
): Filter[] | undefined {
    const isSupported = (filter: Filter) => {
        const field = config.filters.find((candidate) => candidate.id === filter.filterId);
        if (!field || !getOperatorsForFieldType(field.type ?? 'text', field.operators).includes(filter.operator)) {
            return false;
        }
        if (!CLOSED_VALUE_FILTER_IDS.some((id) => id === filter.filterId)) return true;
        const allowed = new Set((Array.isArray(field.options) ? field.options : []).map((option) => option.value));
        const values = Array.isArray(filter.value) ? filter.value : [filter.value];
        return values.every((value) => allowed.has(value));
    };

    return filters?.every(isSupported) ? filters : undefined;
}

/** The values a closed-value field accepts, keyed by filter id (and by its `where` field). */
function closedValueSets<TWhere>(
    config: FilterConfig<TWhere>,
): Map<string, { dbField: string; allowed: Set<unknown> }> {
    const sets = new Map<string, { dbField: string; allowed: Set<unknown> }>();
    for (const id of CLOSED_VALUE_FILTER_IDS) {
        const field = config.filters.find((candidate) => candidate.id === id);
        if (!field) continue;
        const allowed = new Set((Array.isArray(field.options) ? field.options : []).map((option) => option.value));
        sets.set(id, {
            dbField: 'dbField' in field && typeof field.dbField === 'string' ? field.dbField : id,
            allowed,
        });
    }
    return sets;
}

/** A value that is set but outside its closed set. Blank values are an unfinished chip, not stale. */
function isRetired(allowed: Set<unknown>, value: unknown): boolean {
    const values = Array.isArray(value) ? value : [value];
    return values.some((entry) => entry != null && entry !== '' && !allowed.has(entry));
}

function filtersNameRetiredValue(sets: ReturnType<typeof closedValueSets>, filters: unknown): boolean {
    if (!Array.isArray(filters)) return false;
    return filters.some((filter: unknown) => {
        if (filter === null || typeof filter !== 'object') return false;
        const { filterId, value } = filter as { filterId?: unknown; value?: unknown };
        const set = typeof filterId === 'string' ? sets.get(filterId) : undefined;
        return set !== undefined && isRetired(set.allowed, value);
    });
}

/** Every string under a field's clause (`'X'`, `{ in: [...] }`, `{ not: 'X' }`, ...). */
function collectStrings(clause: unknown, into: string[] = []): string[] {
    if (typeof clause === 'string') into.push(clause);
    else if (Array.isArray(clause)) for (const entry of clause) collectStrings(entry, into);
    else if (clause !== null && typeof clause === 'object')
        for (const entry of Object.values(clause)) collectStrings(entry, into);
    return into;
}

/**
 * Walks only the list's own columns and the logical combinators — a relation (e.g. a step)
 * may carry an unrelated `status` of its own.
 */
function whereNamesRetiredValue(sets: ReturnType<typeof closedValueSets>, where: unknown): boolean {
    if (Array.isArray(where)) return where.some((entry) => whereNamesRetiredValue(sets, entry));
    if (where === null || typeof where !== 'object') return false;
    return Object.entries(where).some(([key, clause]) => {
        if (key === 'AND' || key === 'OR' || key === 'NOT') return whereNamesRetiredValue(sets, clause);
        const set = [...sets.values()].find((candidate) => candidate.dbField === key);
        return set !== undefined && collectStrings(clause).some((value) => !set.allowed.has(value));
    });
}

/**
 * CUR-1624 — the reload half of the retired-value guard. `acceptDeepLinkFilters` only sees
 * the URL; a tab that had a since-retired value (COMPLETED) selected restores its filter chips
 * and its query `where` from session path state, which `usePathState` hands back unchecked and
 * `useFilterBuilderWithUrl` deliberately does not replay on mount. The first list request would
 * then carry the retired value and fail with BAD_REQUEST until the user cleared the chip.
 *
 * Call before the list's `usePathState` / `useFilterBuilderWithUrl` read the store: when either
 * restored entry names a closed-value option the list no longer offers, both are discarded
 * together (the chips and the `where` built from them must not diverge) and the list starts
 * from its defaults. Returns whether anything was discarded.
 */
export function discardRetiredRestoredState<TWhere>(
    config: FilterConfig<TWhere>,
    pathname: string,
    keys: { filters: string; query: string },
): boolean {
    const store = usePathStateStore.getState();
    const filtersKey = getPathStateKey(pathname, keys.filters);
    const queryKey = getPathStateKey(pathname, keys.query);
    const sets = closedValueSets(config);

    const storedQuery = store.getState(queryKey)?.value;
    const stale =
        filtersNameRetiredValue(sets, store.getState(filtersKey)?.value) ||
        (storedQuery !== null &&
            typeof storedQuery === 'object' &&
            whereNamesRetiredValue(sets, (storedQuery as { where?: unknown }).where));
    if (!stale) return false;

    store.deleteState(filtersKey);
    store.deleteState(queryKey);
    return true;
}
